import { compare, hash } from "bcryptjs";
import { type ClassValue, clsx } from "clsx";
import { StaticImageData } from "next/image";
import { useTranslations } from "next-intl";
import { twMerge } from "tailwind-merge";

import { generateCSP } from "@/lib/csp";
import { DEFAULT_LOGIN_REDIRECT } from "@/routes";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const getDefaultLoginRedirect = (locale: string) => `/${ locale }${ DEFAULT_LOGIN_REDIRECT == "/" ? "" : DEFAULT_LOGIN_REDIRECT }`;

export interface IOption {
    value: string;
    label: string;
}

export const getAppearanceOptions = (t: ReturnType<typeof useTranslations<"theme">>) => {
    return [
        { value: "light", label: t("light") },
        { value: "dark", label: t("dark") },
        { value: "system", label: t("system") },
    ];
};

export interface ILocaleOption extends IOption {
    flag?: string | StaticImageData;
}

export const getLocaleOptions = (): ILocaleOption[] => {
    return [
        { value: "en", label: "English", flag: "/assets/images/flags/en.png" },
        { value: "fr", label: "Français", flag: "/assets/images/flags/fr.png" },
    ];
};

export const getLocales = () => getLocaleOptions().map(option => option.value);

export async function hashPassword(password: string) {
    const hashedPassword = await hash(password, 12);
    return hashedPassword;
}

export async function isPasswordValid(password: string, hashedPassword: string) {
    const isValid = await compare(password, hashedPassword);
    return isValid;
}

export const getLocaleFromPathname = (pathname: string): string | null => {
    const locales = getLocales();
    const match = pathname.match(new RegExp(`^(/(${ locales.join("|") }))`));
    return match ? match[1] : null;
};

export const { nonce, csp } = generateCSP();

interface IDownloadFile {
    blobParts: BlobPart[];
    blobOptions?: BlobPropertyBag;
    fileName: string;
    onError: (message: string) => void;
}

export const downloadFile = async ({ blobParts, blobOptions, fileName, onError }: IDownloadFile) => {
    try {
        const blob = new Blob(blobParts, blobOptions);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch {
        onError("error.common");
    }
};

export const formatNumber = (number: number) => {
    if (number >= 1e6) {
        const formatted = (number / 1e6).toFixed(1);
        return formatted.endsWith(".0") ? formatted.slice(0, -2) + "M" : formatted + "M";
    } else if (number >= 1e5) {
        const formatted = (number / 1000).toFixed(1);
        return formatted.endsWith(".0") ? formatted.slice(0, -2) + "k" : formatted + "k";
    }

    return number.toLocaleString();
};
